import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { HeartPulse, TriangleAlert, ShieldCheck } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { ROLES } from "../lib/rbac";
import { api } from "../lib/api";

const POLL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Compact status pill that lives in the top-right header for Admin users only.
 *
 * • Polls `/api/admin/health` every 5 minutes.
 * • Shows green (healthy), amber (warning), or red (critical) dot + issue count.
 * • Click → navigates to the System Health page.
 */
export default function HealthBadge() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [status, setStatus] = useState(null); // "healthy" | "warning" | "critical"
    const [count, setCount] = useState(0);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/health");
            setStatus(data?.overall_status || "healthy");
            // Aggregate issue count: risks + prefix mismatches + missing bio + offline devices.
            const c = (data?.known_500_risks?.length || 0)
                + (data?.biometric_id_prefix_mismatches?.count || 0)
                + (data?.employees_missing_biometric_id?.count || 0)
                + (data?.devices_summary?.offline || 0)
                + (data?.pending_exceptions_count || 0);
            setCount(c);
        } catch {
            // Silent — the health page will show the error inline if the user opens it.
        }
    }, []);

    useEffect(() => {
        if (user?.role !== ROLES.KAAFI_ADMIN) return undefined;
        load();
        const id = setInterval(load, POLL_MS);
        const onFocus = () => load();
        window.addEventListener("focus", onFocus);
        return () => {
            clearInterval(id);
            window.removeEventListener("focus", onFocus);
        };
    }, [user?.role, load]);

    if (user?.role !== ROLES.KAAFI_ADMIN || status === null) return null;

    const isHealthy = status === "healthy";
    const isCritical = status === "critical";
    const Icon = isHealthy ? ShieldCheck : (isCritical ? TriangleAlert : HeartPulse);
    const dotColor = isHealthy ? "#16A34A" : (isCritical ? "#DC2626" : "#D97706");
    const label = isHealthy ? "Healthy" : (isCritical ? "Critical" : "Warning");

    return (
        <button
            onClick={() => navigate("/admin/system-health")}
            data-testid="nav-health-badge"
            title={`System status: ${label}${count ? ` · ${count} issue${count === 1 ? "" : "s"}` : ""}`}
            className="inline-flex items-center gap-1.5 h-9 px-2.5 rounded-full border bg-white transition hover:shadow-sm"
            style={{ borderColor: "var(--asl-border)" }}
        >
            <span
                className="inline-block h-2 w-2 rounded-full"
                style={{ backgroundColor: dotColor }}
                aria-hidden
            />
            <Icon className="h-4 w-4 asl-navy-text" />
            {count > 0 && !isHealthy && (
                <span
                    data-testid="nav-health-badge-count"
                    className="mono text-[10px] font-bold px-1.5 py-0.5 rounded-full text-white"
                    style={{ backgroundColor: dotColor }}
                >
                    {count > 99 ? "99+" : count}
                </span>
            )}
        </button>
    );
}
